import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, InlineAlert } from "@ncp/ui";
import type { LetterCitationHold } from "./api";

/**
 * Why a letter cites provisions not yet verified (condition 2.13): each provision it cites that the Department has not yet verified against the
 * Arabic text in the Official Gazette, and any article its template writes out instead of citing the legal rules register. On a held letter the
 * officer reads it with these named and may confirm it; on a sent one they are what it was sent on, recorded in the file's history; on a letter not
 * yet sent – one the officer is composing or revising, or one a step sends (`draft`) – they are named before it is sent.
 */
export function LetterCitationsNotice({ citations, held, draft }: { citations: LetterCitationHold; held: boolean; draft?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  return (
    <InlineAlert tone={held || draft ? "warning" : "info"} title={t("backoffice:decisionControls.letters.title")}>
      <p className="text-xs">{t(draft ? "backoffice:decisionControls.letters.draftHint" : held ? "backoffice:decisionControls.letters.hint" : "backoffice:decisionControls.letters.sentHint")}</p>
      {citations.unverified.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs" data-unverified-provisions={citations.unverified.length}>
          {citations.unverified.map((c) => (
            <li key={c.key} className="flex flex-wrap items-center gap-1.5 rounded-lg bg-white/70 p-2">
              <ScrollText className="size-3.5 shrink-0 text-aegold-700" aria-hidden />
              <span dir="auto">{ar ? c.citedAr : c.cited}</span>
              <span className="text-muted-foreground">· {ar ? c.nameAr : c.name}</span>
              <Badge size="sm" variant="warning">{t(`backoffice:decisionControls.readiness.status.${c.status}`, { defaultValue: c.status })}</Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {citations.writtenOut.length > 0 ? <p className="mt-2 text-xs">{t("backoffice:decisionControls.letters.writtenOut", { articles: citations.writtenOut.join(ar ? "، " : ", ") })}</p> : null}
    </InlineAlert>
  );
}

/** A letter not yet sent, as the officer has it on screen. */
export interface DraftLetter { templateKey?: string | null; subject: string; subjectAr?: string | null; body: string; bodyAr?: string | null }

/** The text once the officer has stopped typing for a moment, so the citations are not read on every keystroke. */
function useSettled<T>(value: T, ms = 600): T {
  const [settled, setSettled] = useState(value);
  const key = JSON.stringify(value);
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(id);
  }, [key, ms]); // eslint-disable-line react-hooks/exhaustive-deps
  return settled;
}

/**
 * The provisions a letter the officer is composing or revising cites that the Department has not yet verified, read by the server from the text as
 * it stands (condition 2.13): "Cites provisions not yet verified by the Department", with each provision, before the letter is sent. Nothing when
 * every provision it cites is verified. Nothing is sent or saved.
 */
export function DraftLetterCitations({ caseId, letter, className }: { caseId: string; letter: DraftLetter | null; className?: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const settled = useSettled(letter);
  const query = useQuery({
    queryKey: ["draft-letter-citations", caseId, settled],
    queryFn: () => api.post<{ citations?: LetterCitationHold | null }>(`/api/cases/${caseId}/correspondence/citations`, settled),
    enabled: !!settled && (settled.body.trim().length > 0 || settled.subject.trim().length > 0),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    retry: false,
  });
  if (!letter) return null;
  const citations = query.data?.citations;
  if (!citations) return query.isLoading ? <p className={className ? `${className} text-[11px] text-muted-foreground` : "text-[11px] text-muted-foreground"}>{t("backoffice:decisionControls.letters.checking")}</p> : null;
  return <div className={className}><LetterCitationsNotice citations={citations} held={false} draft /></div>;
}
